import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '@/lib/apiClient';
import type { DownloadFile } from '@/lib/types';

export interface DownloadListParams {
  search?: string;
  status?: string;
  format?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedDownloadsResponse {
  data: DownloadFile[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const downloadsApi = {
  list: (params?: DownloadListParams) => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    if (params?.format) query.set('format', params.format);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));

    const queryStr = query.toString();
    return call<PaginatedDownloadsResponse>(`/downloads${queryStr ? `?${queryStr}` : ''}`);
  },
  delete: (id: string) => call<{ status: string }>(`/downloads/${id}`, { method: 'DELETE' }),
};

export function useDownloads(params?: DownloadListParams) {
  return useQuery({
    queryKey: ['downloads', 'list', params] as const,
    queryFn: () => downloadsApi.list(params),
  });
}

export function useDeleteDownload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => downloadsApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['downloads', 'list'] });
    },
  });
}
